// Superchat Spec 01 — relayed superchat contract + feature-to-screen control.
//
// Pure wire-format contract, same no-vendor rule as social.ts. A superchat is a
// donation-typed sibling of SocialChatMessage: it rides the SAME `social` topic,
// published by the SAME relay identity, so the existing origin gate covers it with
// zero new trust surface. The bridge (tools/ssn-livekit-bridge) and the client
// both depend only on this module, so the two can never drift on the wire shape.
//
// The feature-control contract lives here too: a separate FEATURE_TOPIC the host
// publishes on to drive the on-air read-aloud graphic. Its receive-side gate is
// the publisher gate (canPublish), so hosts and cohosts can feature but viewers
// never can.

import {
  isChatDeleteMessage,
  isSocialChatMessage,
  type ChatDeleteMessage,
  type SocialChatMessage,
} from './social';

/**
 * One relayed superchat from an external platform. Published on
 * SOCIAL_CHAT_TOPIC alongside SocialChatMessage; the `kind` discriminant tells
 * the two apart. Versioned (`v`) so the renderer can reject/migrate old shapes.
 */
export interface SuperchatMessage {
  /** Wire-format version. Bump on any breaking shape change. */
  v: 1;
  /** Discriminant — distinct from 'social-chat'. */
  kind: 'superchat';
  /** Stable per-message id from the source platform; used for dedupe. */
  id: string;
  /** Source platform slug. In data only — never surfaced (unified-feed rule). */
  platform: string;
  author: {
    name: string;
    handle?: string;
    avatarUrl?: string;
    color?: string;
  };
  /** May be '' — some platforms allow an amount-only superchat. */
  text: string;
  amount: {
    /** Parsed numeric value; 0 when the platform string can't be parsed. */
    value: number;
    /** ISO-ish code or symbol as-given; '' when unknown. */
    currency: string;
    /** The platform's ORIGINAL string — what renders. Never re-formatted. */
    display: string;
  };
  /** Source timestamp, epoch ms. */
  ts: number;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Serialise a superchat to a UTF-8 JSON byte payload for `publishData`. */
export function encodeSuperchat(msg: SuperchatMessage): Uint8Array {
  return encoder.encode(JSON.stringify(msg));
}

/**
 * Structural type guard for the superchat shape. `text` may be empty (amount-only
 * superchats are valid); `amount.display` must be a non-empty string (it's what
 * renders); `amount.value` must be finite and ≥ 0; `amount.currency` may be empty.
 */
export function isSuperchatMessage(value: unknown): value is SuperchatMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  if (m.v !== 1 || m.kind !== 'superchat') return false;
  if (typeof m.id !== 'string' || m.id === '') return false;
  if (typeof m.platform !== 'string' || m.platform === '') return false;
  if (typeof m.text !== 'string') return false; // empty allowed
  if (typeof m.ts !== 'number' || !Number.isFinite(m.ts)) return false;

  const author = m.author;
  if (typeof author !== 'object' || author === null) return false;
  const a = author as Record<string, unknown>;
  if (typeof a.name !== 'string' || a.name === '') return false;
  for (const key of ['handle', 'avatarUrl', 'color'] as const) {
    if (a[key] !== undefined && typeof a[key] !== 'string') return false;
  }

  const amount = m.amount;
  if (typeof amount !== 'object' || amount === null) return false;
  const amt = amount as Record<string, unknown>;
  if (typeof amt.value !== 'number' || !Number.isFinite(amt.value) || amt.value < 0) return false;
  if (typeof amt.currency !== 'string') return false; // empty allowed
  if (typeof amt.display !== 'string' || amt.display === '') return false;

  return true;
}

/**
 * Decode a received `social`-topic payload into whichever event it carries.
 * ONE parse, discriminate on `kind`, delegate to the per-shape guard. Returns
 * null for anything malformed. `decodeSocialChat` stays exported for the bridge/
 * back-compat, but the app consumes this union so there's a single source of
 * truth per shape and no parallel schema.
 */
export function decodeSocialEvent(
  bytes: Uint8Array,
): SocialChatMessage | SuperchatMessage | ChatDeleteMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const kind = (parsed as Record<string, unknown>).kind;
  if (kind === 'social-chat') return isSocialChatMessage(parsed) ? parsed : null;
  if (kind === 'superchat') return isSuperchatMessage(parsed) ? parsed : null;
  if (kind === 'chat-delete') return isChatDeleteMessage(parsed) ? parsed : null;
  return null;
}

// --- Feature-to-screen control -------------------------------------------

/** Data-channel topic the host publishes feature/clear control messages on. */
export const FEATURE_TOPIC = 'feature';

/**
 * Host → room control for the on-air read-aloud graphic. `feature-superchat`
 * puts one superchat on screen (replacing any current one); `feature-clear`
 * dismisses it. One featured superchat at a time. Ephemeral — never persisted.
 */
export type FeatureControlMessage =
  | { v: 1; kind: 'feature-superchat'; superchat: SuperchatMessage; ts: number }
  | { v: 1; kind: 'feature-clear'; ts: number };

/** Serialise a feature-control message to a UTF-8 JSON byte payload. */
export function encodeFeatureControl(msg: FeatureControlMessage): Uint8Array {
  return encoder.encode(JSON.stringify(msg));
}

/**
 * Parse + validate a feature-control payload. Returns null on anything malformed;
 * a `feature-superchat` is rejected unless its embedded superchat passes the same
 * guard the wire uses (no separate validation path).
 */
export function decodeFeatureControl(bytes: Uint8Array): FeatureControlMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const m = parsed as Record<string, unknown>;
  if (m.v !== 1) return null;
  if (typeof m.ts !== 'number' || !Number.isFinite(m.ts)) return null;
  if (m.kind === 'feature-clear') {
    return { v: 1, kind: 'feature-clear', ts: m.ts };
  }
  if (m.kind === 'feature-superchat') {
    if (!isSuperchatMessage(m.superchat)) return null;
    return { v: 1, kind: 'feature-superchat', superchat: m.superchat, ts: m.ts };
  }
  return null;
}
